import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Palette, Radio, User, Mail, Sparkles } from 'lucide-react';
import ThemeToggle from '../common/ThemeToggle';
import LanguageSelector from '../common/LanguageSelector';
import { NavItem } from '../../types';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems: NavItem[] = [
    { path: '/', labelKey: 'nav.socials', icon: 'Sparkles' },
    { path: '/art', labelKey: 'nav.art', icon: 'Palette' },
    { path: '/stream', labelKey: 'nav.stream', icon: 'Radio' },
    { path: '/about', labelKey: 'nav.about', icon: 'User' },
    { path: '/contact', labelKey: 'nav.contact', icon: 'Mail' },
  ];

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Sparkles': return <Sparkles className="w-4 h-4" />;
      case 'Palette': return <Palette className="w-4 h-4" />;
      case 'Radio': return <Radio className="w-4 h-4" />;
      case 'User': return <User className="w-4 h-4" />;
      case 'Mail': return <Mail className="w-4 h-4" />;
      default: return null;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'py-3' 
          : 'py-5'
      } px-4 pointer-events-none`}>
        <div className={`max-w-7xl mx-auto flex items-center justify-between transition-all duration-300 pointer-events-auto ${
          isScrolled 
            ? 'p-2 px-4 rounded-full bg-white/70 dark:bg-slate-950/70 backdrop-blur-xl border border-black/5 dark:border-white/10 shadow-lg' 
            : 'p-0 bg-transparent border-transparent shadow-none'
        }`}>
          
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-3 group shrink-0">
            <img
              src="/Kynothan512.png"
              alt="Logo"
              className={`rounded-full border border-black/10 dark:border-white/20 group-hover:scale-105 transition-transform ${
                isScrolled ? 'w-8 h-8' : 'w-10 h-10'
              }`}
            />
            <span className={`font-bold tracking-tight text-slate-900 dark:text-white group-hover:text-brand-amber transition-colors ${
              isScrolled ? 'text-base' : 'text-lg'
            }`}>
              Kynothan
            </span>
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-black/5 dark:bg-white/10 text-brand-indigo dark:text-brand-amber'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                  }`
                }
              >
                {getIcon(item.icon)}
                <span>{t(item.labelKey)}</span>
              </NavLink>
            ))}
          </nav>

          {/* Controls & Mobile Toggle */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex items-center gap-1">
              <LanguageSelector />
              <ThemeToggle />
            </div>
            
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6 text-slate-900 dark:text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute top-0 right-0 bottom-0 w-3/4 max-w-sm bg-white dark:bg-slate-950 shadow-2xl flex flex-col animate-fade-in">
            <div className="flex items-center justify-between p-5 border-b border-black/5 dark:border-white/10">
              <span className="font-bold text-lg text-slate-900 dark:text-white">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <nav className="flex-1 overflow-y-auto py-4 px-3 flex flex-col gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3.5 rounded-xl font-bold transition-all ${
                      isActive
                        ? 'bg-black/5 dark:bg-white/10 text-brand-indigo dark:text-brand-amber'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                    }`
                  }
                >
                  {getIcon(item.icon)}
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </nav>
            
            <div className="p-5 border-t border-black/5 dark:border-white/10 flex items-center justify-between">
              <LanguageSelector />
              <ThemeToggle />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
